import { useState, useEffect } from 'react';
import { Save, Info } from 'lucide-react';
import { toast } from 'sonner';
import { useCompanySettings, useUpdateCompanySettings } from '@/api/queries/settings.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';

const ROLES = ['OWNER', 'ADMIN', 'HR_MANAGER', 'MANAGER', 'ACCOUNTANT'];

const ROLE_COLORS = { OWNER: '#FF3B30', ADMIN: '#FF6B35', HR_MANAGER: '#007AFF', MANAGER: '#34C759', ACCOUNTANT: '#FF9500' };

const PERMISSION_GROUPS = [
  {
    group: 'Employees',
    perms: [
      { key: 'employees:view', label: 'View employees' },
      { key: 'employees:create', label: 'Add employee' },
      { key: 'employees:edit', label: 'Edit employee' },
      { key: 'employees:delete', label: 'Delete employee' },
    ],
  },
  {
    group: 'Attendance',
    perms: [
      { key: 'attendance:view', label: 'View attendance' },
      { key: 'attendance:mark', label: 'Mark/regularize attendance' },
      { key: 'attendance:export', label: 'Export attendance' },
    ],
  },
  {
    group: 'Payroll',
    perms: [
      { key: 'payroll:view', label: 'View payroll' },
      { key: 'payroll:run', label: 'Run payroll' },
      { key: 'payroll:approve', label: 'Approve payroll' },
    ],
  },
  {
    group: 'Leave',
    perms: [
      { key: 'leave:view', label: 'View leave requests' },
      { key: 'leave:approve', label: 'Approve/reject leave' },
      { key: 'leave:manage_policy', label: 'Manage leave policies' },
    ],
  },
  {
    group: 'Expense',
    perms: [
      { key: 'expense:view', label: 'View expenses' },
      { key: 'expense:approve', label: 'Approve expenses' },
      { key: 'expense:reimburse', label: 'Mark as reimbursed' },
    ],
  },
  {
    group: 'Compliance',
    perms: [
      { key: 'compliance:view', label: 'View compliance' },
      { key: 'compliance:generate', label: 'Generate challans' },
    ],
  },
  {
    group: 'Reports',
    perms: [
      { key: 'reports:view', label: 'View reports' },
      { key: 'reports:download', label: 'Download reports' },
    ],
  },
  {
    group: 'Settings',
    perms: [
      { key: 'settings:view', label: 'View settings' },
      { key: 'settings:edit', label: 'Edit settings' },
      { key: 'settings:manage_roles', label: 'Manage roles' },
    ],
  },
];

const allTrue = Object.fromEntries(PERMISSION_GROUPS.flatMap(g => g.perms.map(p => [p.key, true])));

const DEFAULT_MATRIX = {
  OWNER: allTrue,
  ADMIN: { ...allTrue, 'settings:manage_roles': false },
  HR_MANAGER: {
    'employees:view': true, 'employees:create': true, 'employees:edit': true, 'employees:delete': false,
    'attendance:view': true, 'attendance:mark': true, 'attendance:export': true,
    'payroll:view': true, 'payroll:run': false, 'payroll:approve': false,
    'leave:view': true, 'leave:approve': true, 'leave:manage_policy': true,
    'expense:view': true, 'expense:approve': true, 'expense:reimburse': false,
    'compliance:view': true, 'compliance:generate': false,
    'reports:view': true, 'reports:download': true,
    'settings:view': true, 'settings:edit': false, 'settings:manage_roles': false,
  },
  MANAGER: {
    'employees:view': true, 'employees:create': false, 'employees:edit': false, 'employees:delete': false,
    'attendance:view': true, 'attendance:mark': true, 'attendance:export': false,
    'payroll:view': false, 'payroll:run': false, 'payroll:approve': false,
    'leave:view': true, 'leave:approve': true, 'leave:manage_policy': false,
    'expense:view': true, 'expense:approve': true, 'expense:reimburse': false,
    'compliance:view': false, 'compliance:generate': false,
    'reports:view': true, 'reports:download': false,
    'settings:view': false, 'settings:edit': false, 'settings:manage_roles': false,
  },
  ACCOUNTANT: {
    'employees:view': true, 'employees:create': false, 'employees:edit': false, 'employees:delete': false,
    'attendance:view': true, 'attendance:mark': false, 'attendance:export': true,
    'payroll:view': true, 'payroll:run': true, 'payroll:approve': true,
    'leave:view': true, 'leave:approve': false, 'leave:manage_policy': false,
    'expense:view': true, 'expense:approve': false, 'expense:reimburse': true,
    'compliance:view': true, 'compliance:generate': true,
    'reports:view': true, 'reports:download': true,
    'settings:view': false, 'settings:edit': false, 'settings:manage_roles': false,
  },
};

export default function RolesSettingsPage() {
  const { data } = useCompanySettings();
  const { mutate, isPending } = useUpdateCompanySettings();
  const [matrix, setMatrix] = useState(DEFAULT_MATRIX);

  useEffect(() => {
    const saved = data?.data?.settings?.role_permissions;
    if (saved) setMatrix(prev => ({ ...prev, ...saved }));
  }, [data]);

  const toggle = (role, key) => {
    if (role === 'OWNER') return;
    setMatrix(m => ({ ...m, [role]: { ...m[role], [key]: !m[role][key] } }));
  };

  const handleSave = () => {
    mutate({ settings: { role_permissions: matrix } }, {
      onSuccess: () => toast.success('Role permissions saved'),
      onError: () => toast.error('Failed to save'),
    });
  };

  return (
    <div className="space-y-5 fade-in">
      <PageHeader
        title="Roles & Permissions"
        breadcrumb={[{ label: 'Settings', href: '/settings' }, { label: 'Roles & Permissions' }]}
      />

      <div className="flex items-start gap-2 px-4 py-3 rounded-[10px] bg-blue-50 dark:bg-blue-900/20 border border-blue-200 text-[13px] text-blue-700">
        <Info size={14} className="mt-0.5 shrink-0"/>
        <p>OWNER has all permissions and cannot be modified. Changes affect all users with that role.</p>
      </div>

      <div className="card p-0 overflow-x-auto">
        <table className="w-full min-w-[700px]">
          <thead>
            <tr className="border-b border-[var(--border)]">
              <th className="text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)] px-5 py-3 w-52">Permission</th>
              {ROLES.map(r => (
                <th key={r} className="px-4 py-3 text-center">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold"
                    style={{ backgroundColor: ROLE_COLORS[r] + '20', color: ROLE_COLORS[r] }}>
                    {r.replace('_',' ')}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PERMISSION_GROUPS.map(({ group, perms }) => (
              <>
                <tr key={group} className="bg-[var(--bg-muted)]">
                  <td colSpan={6} className="px-5 py-2 text-[11px] font-bold uppercase tracking-wider text-[var(--text-tertiary)]">{group}</td>
                </tr>
                {perms.map(p => (
                  <tr key={p.key} className="border-t border-[var(--border)] hover:bg-[var(--bg-muted)]/50">
                    <td className="px-5 py-3 text-[13px] text-[var(--text-primary)]">{p.label}</td>
                    {ROLES.map(r => (
                      <td key={r} className="px-4 py-3 text-center">
                        <button
                          onClick={() => toggle(r, p.key)}
                          disabled={r === 'OWNER'}
                          className={`w-5 h-5 rounded-[4px] border-2 flex items-center justify-center mx-auto transition-colors ${
                            matrix[r]?.[p.key]
                              ? 'border-[var(--accent)] bg-[var(--accent)]'
                              : 'border-[var(--border)] bg-transparent'
                          } ${r === 'OWNER' ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:border-[var(--accent)]'}`}>
                          {matrix[r]?.[p.key] && <span className="text-white text-[10px] font-bold leading-none">✓</span>}
                        </button>
                      </td>
                    ))}
                  </tr>
                ))}
              </>
            ))}
          </tbody>
        </table>
      </div>

      <Button icon={Save} onClick={handleSave} disabled={isPending}>{isPending ? 'Saving…' : 'Save Permissions'}</Button>
    </div>
  );
}
